import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';

import { AVERAGE_HOURS_IN_MONTH, SECONDS_IN_HOUR } from '@cast/constants';
import { DurationString } from '@cast/shared-types';
import { durationToUnit } from '@cast/utils';

import {
  TickFormatterFn,
  TickSettings,
  TicksConfig,
  TimeTicksResolver,
} from './types';
import { getDataRangeInMilliseconds } from '../_utils';

/**
 * Returns a function that resolves the ticks for a given time series and tick gap.
 */
export const getTickResolver =
  <T>(tickGap: DurationString): TimeTicksResolver<T> =>
  (timeSeries, unixDataKey, _timezone) => {
    if (!timeSeries.length) {
      return [];
    }

    const sortedTimeSeries = sortBy(timeSeries, unixDataKey, 'asc');
    const firstTimestamp = sortedTimeSeries[0][unixDataKey] as number;
    const lastTimestamp = sortedTimeSeries[sortedTimeSeries.length - 1][
      unixDataKey
    ] as number;
    const gapInSeconds = durationToUnit(tickGap, 's');
    const isGapInMonth =
      gapInSeconds >= SECONDS_IN_HOUR * AVERAGE_HOURS_IN_MONTH;

    const ticks: number[] = [];
    let current = dayjs.unix(firstTimestamp);

    while (current.unix() <= lastTimestamp) {
      ticks.push(current.unix());

      if (isGapInMonth) {
        // need special handling for month gaps because each month have different length of seconds
        current = current.add(1, 'month');
      } else {
        current = current.add(gapInSeconds, 's');
      }
    }

    return ticks;
  };

/**
 * Returns a function that formats a Unix timestamp to a given format and timezone.
 */
export const getTickFormatter =
  (format: string): TickFormatterFn =>
  ({ unixTimestamp, timezone }) =>
    dayjs.unix(unixTimestamp).tz(timezone).format(format);

/**
 * Default ticks config for the TimeXAxis component.
 *
 * Maps data range durations to tick settings (tick placement + label format).
 * The first matching entry (where data range <= key) is used:
 *
 * | Range        | Tick interval | Label format    | Example       |
 * |--------------|---------------|-----------------|---------------|
 * | up to 5 min  | 1 minute      | `hh:mm:ss A`   | 01:30:45 PM   |
 * | up to 1 h    | 10 minutes    | `hh:mm A`      | 01:30 PM      |
 * | up to 6 h    | 30 minutes    | `hh:mm A`      | 01:30 PM      |
 * | up to 1 day  | 1 hour        | `hh A`         | 01 PM         |
 * | up to 3 mo   | 1 day         | `MMM DD`       | Jan 15        |
 * | up to 1 year | 1 month       | `MMM`          | Jan           |
 *
 * Can be overridden via the `ticksConfig` prop on `TimeXAxis`.
 */
export const defaultTimeTicksConfig: TicksConfig<any> = {
  // up to 5 mins
  '5m': {
    timeTickFormatter: getTickFormatter('hh:mm:ss A'),
    resolveTimeTicks: getTickResolver('1minutes'),
  },
  // up to 1 hour
  '1h': {
    timeTickFormatter: getTickFormatter('hh:mm A'),
    resolveTimeTicks: getTickResolver('10minutes'),
  },
  // up to 6 hours
  '6h': {
    timeTickFormatter: getTickFormatter('hh:mm A'),
    resolveTimeTicks: getTickResolver('30minutes'),
  },
  // up to 24 hours
  '1d': {
    timeTickFormatter: getTickFormatter('hh A'),
    resolveTimeTicks: getTickResolver('1hour'),
  },
  // up to 3 months
  '3months': {
    timeTickFormatter: getTickFormatter('MMM DD'),
    resolveTimeTicks: getTickResolver('1day'),
  },
  // up to 1 year
  '1y': {
    timeTickFormatter: getTickFormatter('MMM'),
    resolveTimeTicks: getTickResolver('1month'),
  },
};

// anything above 1 year
const tickConfigFallback: TickSettings<any> = {
  timeTickFormatter: getTickFormatter('YYYY-MM-DD'),
  resolveTimeTicks: getTickResolver('1month'),
};

export const determineTickConfig = <T>({
  ticksConfig,
  chartData,
  unixDataKey,
}: {
  ticksConfig: TicksConfig<T>;
  chartData: T[];
  unixDataKey: keyof T;
}): TickSettings<T> => {
  const { range: rangeInMs } = getDataRangeInMilliseconds(
    chartData,
    unixDataKey
  );

  const sortedEntries = Object.entries(ticksConfig)
    .map(([rangeStr, value]) => ({
      range: durationToUnit(rangeStr as DurationString, 'ms'),
      value,
    }))
    .sort((a, b) => a.range - b.range);

  for (const { range, value } of sortedEntries) {
    if (rangeInMs <= range) {
      return value;
    }
  }

  return sortedEntries.at(-1)?.value ?? tickConfigFallback;
};
